import '@xyflow/react/dist/style.css';

import type { ReactFlowInstance } from '@xyflow/react';
import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
} from '@xyflow/react';
import { useRef } from 'react';
import { useWorkflowStore } from '../../store/workflowStore';
import type { StepNodeType, WorkflowEdge, WorkflowNode } from '../../types';
import { STEP_NODE_META } from '../../types';
import { BaseNode } from './nodes/BaseNode';

const NODE_TYPES = { stepNode: BaseNode };

function WorkflowCanvasInner() {
  const nodes = useWorkflowStore((s) => s.nodes);
  const edges = useWorkflowStore((s) => s.edges);
  const onNodesChange = useWorkflowStore((s) => s.onNodesChange);
  const onEdgesChange = useWorkflowStore((s) => s.onEdgesChange);
  const onConnect = useWorkflowStore((s) => s.onConnect);
  const addNode = useWorkflowStore((s) => s.addNode);
  const selectNode = useWorkflowStore((s) => s.selectNode);

  const rfRef = useRef<ReactFlowInstance<WorkflowNode, WorkflowEdge> | null>(null);

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    const stepType = e.dataTransfer.getData('application/workflowNodeType') as StepNodeType;
    if (!stepType || !rfRef.current) return;

    const position = rfRef.current.screenToFlowPosition({
      x: e.clientX,
      y: e.clientY,
    });

    addNode({
      id: crypto.randomUUID(),
      type: 'stepNode',
      position,
      data: {
        label: STEP_NODE_META[stepType].label,
        stepType,
      },
    });
  }

  return (
    <section
      aria-label="Workflow canvas"
      className="flex-1 h-full"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={(_, node) => selectNode(node.id)}
        onPaneClick={() => selectNode(null)}
        onInit={(instance) => {
          rfRef.current = instance;
        }}
        nodeTypes={NODE_TYPES}
        fitView
        deleteKeyCode="Delete"
        className="bg-[#F8FAFF]"
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#CBD5E1" />
        <Controls />
        <MiniMap
          nodeColor={() => '#E0E7FF'}
          maskColor="rgba(248,250,255,0.7)"
          className="!border !border-placeholder/40 !rounded-lg"
        />
      </ReactFlow>
    </section>
  );
}

export function WorkflowCanvas() {
  return (
    <ReactFlowProvider>
      <WorkflowCanvasInner />
    </ReactFlowProvider>
  );
}
